/**
 * SprintRetroStore — the Sprint Retro feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY sprint-retro module that talks to the API,
 * and it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<SprintRetro>('sprintRetros')` + `api.identityProvider`). It
 * never imports ApiClient or calls fetch. React hooks call this store; pages
 * call the hook.
 *
 * Persistence model (mirrors {@link ../../preplanning/prePlanStore}): each retro
 * is one document with id `retro_<sprintId>` in the real 'sprintRetros'
 * generic-store collection (DDB pointer row + S3 body). Keying by sprintId means
 * a sprint has AT MOST ONE retro. The short discovery fields
 * (sprintId/sprintName + createdBy + timestamps + deleted flag) are mirrored
 * onto the pointer row's index attributes so {@link listSummaries} reconstructs
 * summaries directly from pointer rows WITHOUT hydrating S3 bodies; the large
 * arrays (wentWell / didntGoWell / kudos) live only in the S3 body and are read
 * on demand by {@link getBySprint}. Tenant scoping is automatic — DocumentStore
 * routes by the active team bundle.
 *
 * Save is an UPSERT: {@link saveDraft} creates the retro on first save and
 * updates it thereafter, preserving createdBy/createdAt.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { DocumentStore, EngineeringVectorApi } from '../../../api';
import type { SprintRetro, SprintRetroDraft, SprintRetroSummary } from './types';
import {
  SPRINT_RETROS_COLLECTION,
  SPRINT_RETRO_ROW,
  cleanRetroDraft,
  retroDocId,
  retroIndexAttributes,
  retroSummaryFromRow,
} from './sprintRetroModel';

export class SprintRetroStore {
  private readonly docs: DocumentStore<SprintRetro>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<SprintRetro>(SPRINT_RETROS_COLLECTION);
  }

  /** Lists all non-deleted retro summaries (defensively scoped to this collection). */
  async listSummaries(): Promise<SprintRetroSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[SPRINT_RETRO_ROW.collection] === SPRINT_RETROS_COLLECTION &&
          r[SPRINT_RETRO_ROW.deleted] !== 'true' &&
          !!r[SPRINT_RETRO_ROW.primaryIdentifier],
      )
      .map((r) => retroSummaryFromRow(r));
  }

  /** Loads a single retro record by id (hydrates the S3 body). */
  async get(id: string): Promise<SprintRetro | undefined> {
    return this.docs.get(id);
  }

  /**
   * Loads a sprint's retro (hydrates the S3 body), or undefined when the sprint
   * has no retro yet. Returns undefined for soft-deleted retros too.
   */
  async getBySprint(sprintId: string): Promise<SprintRetro | undefined> {
    if (!sprintId) return undefined;
    const existing = await this.docs.get(retroDocId(sprintId));
    if (!existing || existing.deleted) return undefined;
    return existing;
  }

  /**
   * Upserts a sprint's retro from a draft. Cleans blank scaffolding rows, seeds
   * id (keyed by sprintId), preserves createdBy/createdAt on update, refreshes
   * lastUpdated, re-mirrors index attributes, and returns the saved record.
   */
  async saveDraft(draft: SprintRetroDraft): Promise<SprintRetro> {
    const clean = cleanRetroDraft(draft);
    const id = retroDocId(clean.sprintId);
    const existing = await this.docs.get(id).catch(() => undefined);
    const identity = await this.api.identityProvider.getIdentity();
    const now = new Date().toISOString();

    const retro: SprintRetro = {
      id,
      sprintId: clean.sprintId.trim(),
      sprintName: clean.sprintName?.trim() || undefined,
      wentWell: clean.wentWell,
      didntGoWell: clean.didntGoWell,
      kudos: clean.kudos,
      createdBy: existing?.createdBy ?? identity.alias,
      createdAt: existing?.createdAt ?? now,
      lastUpdated: now,
      deleted: false,
    };

    if (existing) {
      await this.docs.update(id, retro, retroIndexAttributes(retro));
    } else {
      await this.docs.save(id, retro, retroIndexAttributes(retro));
    }
    return retro;
  }

  /**
   * Soft-deletes a sprint's retro: fetches it, flags it deleted, and updates both
   * the S3 body and the pointer row so list queries filter it out.
   */
  async softDelete(sprintId: string): Promise<void> {
    const id = retroDocId(sprintId);
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: SprintRetro = { ...existing, deleted: true };
    await this.docs.update(id, deleted, retroIndexAttributes(deleted));
  }
}
